<template>
  <div class="pos-wrapper">
    <!-- Top Header (Green Brand) -->
    <div class="pos-header elevation-2 px-4 py-2 d-flex align-center justify-space-between">
         <!-- Brand -->
         <div class="d-flex align-center mr-8">
            <div class="mr-2 d-flex align-center justify-center">
               <img 
                 :src="require('@/assets/images/svg/branco.svg')" 
                 alt="Ezcont" 
                 style="height: 32px;"
               />
            </div>
         </div>

         <!-- Search Bar (Central) - Autocomplete -->
         <div class="flex-grow-1 mx-4" style="max-width: 600px;">
           <v-autocomplete
              v-model="selectedProduct"
              :items="products"
              :loading="loading"
              :search-input.sync="searchQuery"
              placeholder="Buscar por nome, código de barras ou SKU"
              item-text="name"
              item-value="id"
              return-object
              outlined
              dense
              hide-details
              hide-no-data
              clearable
              class="rounded-lg search-field-white"
              background-color="white"
              :prepend-inner-icon="icons.mdiMagnify"
              @change="onProductSelected"
              @keydown.enter="searchProduct"
              autofocus
              :menu-props="{ maxHeight: 400, offsetY: true }"
           >
              <template v-slot:item="{ item }">
                 <v-list-item-avatar tile size="40" class="rounded grey lighten-4">
                    <v-img v-if="item.image" :src="item.image" contain></v-img>
                    <v-icon v-else color="grey darken-1">{{ icons.mdiPackageVariant }}</v-icon>
                 </v-list-item-avatar>
                 <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                       <span class="grey--text">SKU: {{ item.sku || '---' }}</span>
                       <span class="ml-3 success--text font-weight-bold">{{ (item.value / 100) | moneyBr }}</span>
                    </v-list-item-subtitle>
                 </v-list-item-content>
                 <v-list-item-action>
                    <v-btn small color="success" depressed>
                       <v-icon small left>{{ icons.mdiCartPlus }}</v-icon>
                       Adicionar
                    </v-btn>
                 </v-list-item-action>
              </template>
              <template v-slot:append>
                 <div class="d-flex align-center">
                    <v-icon>{{ icons.mdiBarcodeScan }}</v-icon>
                 </div>
              </template>
           </v-autocomplete>
         </div>

         <!-- Right Actions (Fullscreen, User, Theme) -->
         <div class="d-flex align-center">
             <v-btn icon dark @click="toggleFullscreen">
                 <v-icon>{{ isFullscreen ? icons.mdiFullscreenExit : icons.mdiFullscreen }}</v-icon>
             </v-btn>
             <v-avatar size="36" color="white" class="ml-2">
                 <img :src="require('@/assets/images/avatars/1.png')" alt="User" />
             </v-avatar>
             <div class="ml-2 font-weight-bold white--text hidden-sm-and-down">
                 {{ userName }}
             </div>
             <v-btn icon dark class="ml-4" to="/home">
                 <v-icon>{{ icons.mdiPower }}</v-icon>
             </v-btn>
         </div>
      </div>

      <!-- Main Content Area -->
      <div class="pos-content">
         <!-- Left: Order Details -->
         <div class="pos-order-panel">
            <POSOrderDetails
               :items="cartItems"
               @update-qty="updateCartQty"
               @remove="removeFromCart"
               @clear-all="clearCart"
            />
         </div>

         <!-- Right: Invoice Sidebar -->
         <div class="pos-sidebar-panel">
             <POSInvoiceSidebar
                :selectedCustomer.sync="selectedCustomer"
                :customerResults="customerResults"
                :customerLoading="customerLoading"
                :subtotal="subtotal"
                :discount="discount"
                :total="total"
                @search-customer="searchCustomers"
                @open-customer-modal="openCustomerModal"
                @open-discount="openDiscountModal"
                @cancel="clearCart"
                @request-payment="handlePaymentRequest"
             />
         </div>
      </div>

      <!-- Modals -->
      <POSPaymentModal
        ref="paymentModal"
        :total-to-pay="total"
        @finish="finishSale"
      />

      <POSCustomerModal
        ref="customerModal"
        @created="onCustomerCreated"
      />

      <POSDiscountModal
        ref="discountModal"
        @apply="applyDiscount"
      />
  </div>
</template>

<script>
import POSCart from "@/components/pos/POSCart.vue";
import POSPaymentModal from "@/components/pos/POSPaymentModal.vue";
import POSCustomerModal from "@/components/pos/POSCustomerModal.vue";
import POSDiscountModal from "@/components/pos/POSDiscountModal.vue";
import POSOrderDetails from "@/components/pos/POSOrderDetails.vue";
import POSInvoiceSidebar from "@/components/pos/POSInvoiceSidebar.vue";
import _ from "lodash";
import { 
  mdiCartPlus, 
  mdiFullscreen, 
  mdiFullscreenExit, 
  mdiClose, 
  mdiMagnify, 
  mdiPackageVariant, 
  mdiImageOffOutline, 
  mdiPlus, 
  mdiArrowRight,
  mdiAccountSearch,
  mdiAccountPlus,
  mdiPencil,
  mdiBasket,
  mdiBarcodeScan,
  mdiPower
} from '@mdi/js';

export default {
  name: "POS",
  components: { 
    POSCart, 
    POSPaymentModal, 
    POSCustomerModal, 
    POSDiscountModal,
    POSOrderDetails,
    POSInvoiceSidebar
  },
  data() {
    return {
      searchQuery: "",
      selectedProduct: null,
      products: [],
      cartItems: [],
      loading: false,
      discount: 0,
      isFullscreen: false,

      // Customer Data
      selectedCustomer: null,
      customerSearch: null,
      customerResults: [],
      customerLoading: false,

      icons: {
        mdiCartPlus,
        mdiFullscreen,
        mdiFullscreenExit,
        mdiClose,
        mdiMagnify,
        mdiPackageVariant,
        mdiImageOffOutline,
        mdiPlus,
        mdiArrowRight,
        mdiAccountSearch,
        mdiAccountPlus,
        mdiPencil,
        mdiBasket,
        mdiBarcodeScan,
        mdiPower
      }
    };
  },
  computed: {
    subtotal() {
      return this.cartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
    },
    total() {
      return Math.max(0, this.subtotal - this.discount);
    },
    userName() {
      return this.$store?.state?.user?.name || 'Usuário';
    }
  },
  watch: {
    searchQuery: _.debounce(function() {
       this.getProducts();
    }, 500),
    customerSearch(val) {
      if(!val || val === this.selectedCustomer?.name) return;
      this.searchCustomers(val);
    }
  },
  mounted() {
    this.getProducts();
    this.enterFullscreen();
  },
  beforeDestroy() {
    this.exitFullscreen();
  },
  methods: {
    searchCustomers: _.debounce(function(val) {
        if(!val) return;
        this.customerLoading = true;
        this.$http.index("/customer", { q: val })
          .then(res => {
             this.customerResults = res.data.data;
          })
          .catch(console.error)
          .finally(() => this.customerLoading = false);
    }, 500),
    
    openCustomerModal() {
       this.$refs.customerModal.open();
    },
    
    onCustomerCreated(customer) {
       this.selectedCustomer = customer;
       this.customerResults = [customer]; // Ensure it's in the list
    },

    onProductSelected(product) {
       if (product) {
          this.addToCart(product);
          this.$nextTick(() => {
             this.selectedProduct = null;
             this.searchQuery = "";
          });
       }
    },

    toggleFullscreen() {
      if (!document.fullscreenElement) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }
    },
    enterFullscreen() {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().then(() => { this.isFullscreen = true; }).catch((err) => {
          console.log(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
        });
      }
    },
    exitFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen().then(() => { this.isFullscreen = false; });
      }
    },
    getProducts() {
      this.loading = true;
      this.$http
        .$get("/pdv/products", { search: this.searchQuery })
        .then((res) => {
          this.products = res.data || [];
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    searchProduct() {
      const query = this.searchQuery;
      if(!query) {
          this.getProducts();
          return;
      }
      
      // Check if it's a barcode (numeric and reasonable length, e.g., > 7 for EAN8)
      const isBarcode = /^\d{8,14}$/.test(query);

      if(isBarcode) {
          this.loading = true;
          this.$http.$get(`/pdv/products/barcode/${query}`)
            .then(res => {
                 const product = res.data; // Assuming return single object or null
                 if(product) {
                     this.addToCart(product);
                     this.searchQuery = ""; // Clear input for next scan
                     this.$toast.success("Produto adicionado!");
                 } else {
                     this.$toast.warning("Produto não encontrado por código de barras.");
                     // Fallback to normal search if needed, but usually barcode search is specific
                 }
            })
            .catch(err => {
                // If 404
                this.$toast.warning("Produto não encontrado.");
                console.error(err);
            })
            .finally(() => this.loading = false);
      } else {
          // Normal search
          this.getProducts();
      }
    },
    addToCart(product) {
       const existing = this.cartItems.find(i => i.id === product.id);
       if(existing) {
         existing.qty++;
       } else {
         this.cartItems.push({
           id: product.id,
           name: product.name,
           sku: product.sku,
           price: product.value, // value is in cents
           qty: 1
         });
       }
       this.$toast.success("Produto adicionado!");
    },
    removeFromCart(item) {
      this.cartItems = this.cartItems.filter(i => i.id !== item.id);
    },
    updateCartQty({ item, qty }) {
      if(qty <= 0) {
        this.removeFromCart(item);
      } else {
        const cartItem = this.cartItems.find(i => i.id === item.id);
        if(cartItem) cartItem.qty = qty;
      }
    },
    clearCart() {
       if(confirm("Tem certeza que deseja limpar o carrinho?")) {
         this.cartItems = [];
         this.discount = 0;
       }
    },
    openDiscountModal() {
       if(this.cartItems.length === 0) {
           this.$toast.warning("Adicione itens ao carrinho primeiro.");
           return;
       }
       this.$refs.discountModal.open(this.discount, this.subtotal);
    },
    applyDiscount(amount) {
       this.discount = amount;
       this.$toast.success("Desconto aplicado!");
    },
    openPaymentModal() {
       this.$refs.paymentModal.open();
    },
    finishSale(paymentData) {
       this.loading = true;

       const saleData = {
           items: this.cartItems.map(item => ({
             product_id: item.id,
             quantity: item.qty,
             price: item.price
           })),
           discount: this.discount,
           total: this.total,
           customer_id: this.selectedCustomer ? this.selectedCustomer.id : null,
           payment_method: paymentData.method,
           payment_details: {
               received: paymentData.received,
               change: paymentData.change
           },
           emit_invoice: paymentData.emit_invoice ? true : false
       };
          
       this.$http.$post("/pdv/sales", saleData)
         .then(res => {
             if(paymentData.emit_invoice) {
                this.$toast.success("Venda finalizada e Cupom emitido!");
             } else {
                this.$toast.success("Venda finalizada com sucesso!");
             }
             
             // Reset state
             this.cartItems = [];
             this.discount = 0;
             this.searchQuery = "";
             this.selectedCustomer = null; 
             this.$refs.paymentModal.amountReceived = null;
         })
         .catch(err => {
             console.error(err);
             const msg = err.response?.data?.message || "Erro ao finalizar venda.";
             this.$toast.error(msg);
             
             // If validation error, maybe show details
             if(err.response?.status === 422) {
                 const errors = err.response.data.errors;
                 Object.values(errors).flat().forEach(e => this.$toast.error(e));
             }
         })
         .finally(() => {
             this.loading = false;
         });
    },
    handlePaymentRequest({ method, emit_invoice }) {
       // Open the payment modal but pre-set values
       // We'll use $refs to set data in the modal
       const modal = this.$refs.paymentModal;
       modal.paymentMethod = method;
       modal.emitInvoice = emit_invoice;
       modal.dialog = true;
       // If cash, user needs to type received. If card/pix, maybe auto confirm? 
       // For safety, let's just open it. 
       // Or if Card/Pix, we could simulate "Received = Total" and "Confirm" automatically if you wanted super fast flow.
       // Given the "High Fidelity" request usually implies speed:
       if(method === 'money') {
           // Focus on input
           this.$nextTick(() => {
               // Logic to focus input inside modal if possible
           });
       } else {
           // Auto-fill received amount for convenience
           modal.amountReceived = (this.total / 100).toFixed(2);
       }
    }
  }
};
</script>

<style scoped>
/* Layout principal do PDV */
.pos-wrapper {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background-color: #f3f4f6;
}

/* Header verde escuro */
.pos-header {
  background: linear-gradient(135deg, #009ddb 0%, #009ddb 100%);
  height: 70px;
  min-height: 70px;
  width: 100%;
  flex-shrink: 0;
}

/* Área de conteúdo principal */
.pos-content {
  display: flex;
  flex: 1;
  overflow: hidden;
  padding: 8px;
  gap: 8px;
}

/* Painel de detalhes do pedido (esquerda) */
.pos-order-panel {
  flex: 2;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

/* Painel do cupom/sidebar (direita) */
.pos-sidebar-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 350px;
  max-width: 400px;
}

.search-field-white ::v-deep .v-input__slot {
  background-color: white !important;
}

/* Estilo do autocomplete de produtos */
.search-field-white ::v-deep .v-list-item {
  min-height: 60px;
}

.search-field-white ::v-deep .v-list-item__avatar {
  margin-right: 12px;
}

/* Specific button styling */
.v-btn.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.4), 0 4px 6px -2px rgba(16, 185, 129, 0.2) !important;
}
</style>
